import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Heart, CheckCircle, MessageCircle, Award } from 'lucide-react';

interface ServiceClientCloudModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ServiceClientCloudModal({ isOpen, onClose }: ServiceClientCloudModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-pink-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-pink-500/30 shadow-2xl shadow-pink-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-pink-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl">
                    <Users className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
                      Service Client & Support
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Service humain exceptionnel - Très apprécié au Québec et au Canada
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-pink-600/20 via-rose-600/20 to-pink-600/20 rounded-xl p-8 border-2 border-pink-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Service Humain : Notre Force
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  C'est notre <strong className="text-white">service humain exceptionnel</strong> qui rend notre cloud privé une option 
                  <strong className="text-pink-400"> très appréciée des clients du Québec et du Canada</strong>. 
                  Support réactif, personnalisé, et dédié. C'est ce qui fait la différence.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-6 border border-pink-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Heart className="w-7 h-7 text-pink-400" />
                    Support Réactif et Personnalisé
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Notre équipe offre un support exceptionnel :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Réactivité</strong> - Réponse rapide aux demandes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Personnalisation</strong> - Approche adaptée à chaque client</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Disponibilité</strong> - Support accessible quand vous en avez besoin</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Expertise</strong> - Équipe compétente et formée</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-xl p-6 border border-rose-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <MessageCircle className="w-7 h-7 text-rose-400" />
                    Communication Transparente
                  </h4>
                  <p className="text-gray-300 mb-4">
                    Communication claire et transparente avec nos clients :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Mises à jour régulières</strong> - Information proactive</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Explications claires</strong> - Compréhension facilitée</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Écoute active</strong> - Compréhension des besoins</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-rose-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Feedback valorisé</strong> - Amélioration continue</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-7 h-7 text-purple-400" />
                    Différenciateur Concurrentiel
                  </h4>
                  <p className="text-gray-300">
                    Bien que nous ne soyons pas certifiés SOC2 ou TIER X, notre <strong className="text-white">service humain</strong> 
                    est un <strong className="text-purple-400">différenciateur concurrentiel puissant</strong>. 
                    Nos clients du Québec et du Canada apprécient cette dimension humaine qui fait toute la différence 
                    dans leur expérience avec notre cloud privé.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl p-6 border border-yellow-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Users className="w-7 h-7 text-yellow-400" />
                    Relation de Confiance
                  </h4>
                  <p className="text-gray-300">
                    Nous construisons des <strong className="text-white">relations de confiance à long terme</strong> avec nos clients. 
                    C'est cette approche humaine, cette attention personnalisée, qui fait que nos clients nous choisissent 
                    et nous restent fidèles. C'est l'essence de l'excellence BZ Inc.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-pink-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all duration-200 font-semibold shadow-lg shadow-pink-500/20 hover:shadow-pink-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












